<template>
  <b-col
    cols="12"
    lg="12"
    class="mb-4"
  >
    <b-card>
      <template #header>
        <h4 class="mb-0">
          Análise RFM - Segmentação de Clientes
        </h4>
      </template>

      <!-- Debug -->
      <div
        v-if="!matrizRFM || Object.keys(matrizRFM).length === 0"
        class="text-center p-4"
      >
        <p class="text-muted">
          ⚠️ Matriz RFM vazia. Aguardando dados...
        </p>
        <small class="text-muted">Segmentos: {{ Object.keys(segmentos).length }}</small>
      </div>

      <div
        v-else
        class="rfm-matrix-container"
      >
        <!-- Matriz RFM -->
        <div class="rfm-grid">
          <!-- Eixo Y - Frequência -->
          <div class="rfm-axis rfm-axis-y">
            <div class="axis-label">
              FREQUÊNCIA / MONETÁRIO (MÉDIA)
            </div>
          </div>

          <!-- Grade RFM Dinâmica -->
          <div class="rfm-cells">
            <!-- Linha 5 (F=5) -->
            <div class="rfm-row">
              <div
                v-for="r in [1,2,3,4,5]"
                :key="`5-${r}`"
                :class="['rfm-cell', getSegmentClass(r, 5)]"
                :data-segment="getSegmentName(r, 5)"
                :title="`R=${r}, F=5: ${getCellTotal(r, 5)} clientes`"
                @click="$emit('cell-click', r, 5)"
              >
                <div class="cell-number">
                  {{ getCellTotal(r, 5) }}
                </div>
                <div class="cell-label">
                  {{ getSegmentLabel(r, 5) }}
                </div>
              </div>
            </div>

            <!-- Linha 4 (F=4) -->
            <div class="rfm-row">
              <div
                v-for="r in [1,2,3,4,5]"
                :key="`4-${r}`"
                :class="['rfm-cell', getSegmentClass(r, 4)]"
                :data-segment="getSegmentName(r, 4)"
                :title="`R=${r}, F=4: ${getCellTotal(r, 4)} clientes`"
                @click="$emit('cell-click', r, 4)"
              >
                <div class="cell-number">
                  {{ getCellTotal(r, 4) }}
                </div>
                <div class="cell-label">
                  {{ getSegmentLabel(r, 4) }}
                </div>
              </div>
            </div>

            <!-- Linha 3 (F=3) -->
            <div class="rfm-row">
              <div
                v-for="r in [1,2,3,4,5]"
                :key="`3-${r}`"
                :class="['rfm-cell', getSegmentClass(r, 3)]"
                :data-segment="getSegmentName(r, 3)"
                :title="`R=${r}, F=3: ${getCellTotal(r, 3)} clientes`"
                @click="$emit('cell-click', r, 3)"
              >
                <div class="cell-number">
                  {{ getCellTotal(r, 3) }}
                </div>
                <div class="cell-label">
                  {{ getSegmentLabel(r, 3) }}
                </div>
              </div>
            </div>

            <!-- Linha 2 (F=2) -->
            <div class="rfm-row">
              <div
                v-for="r in [1,2,3,4,5]"
                :key="`2-${r}`"
                :class="['rfm-cell', getSegmentClass(r, 2)]"
                :data-segment="getSegmentName(r, 2)"
                :title="`R=${r}, F=2: ${getCellTotal(r, 2)} clientes`"
                @click="$emit('cell-click', r, 2)"
              >
                <div class="cell-number">
                  {{ getCellTotal(r, 2) }}
                </div>
                <div class="cell-label">
                  {{ getSegmentLabel(r, 2) }}
                </div>
              </div>
            </div>

            <!-- Linha 1 (F=1) -->
            <div class="rfm-row">
              <div
                v-for="r in [1,2,3,4,5]"
                :key="`1-${r}`"
                :class="['rfm-cell', getSegmentClass(r, 1)]"
                :data-segment="getSegmentName(r, 1)"
                :title="`R=${r}, F=1: ${getCellTotal(r, 1)} clientes`"
                @click="$emit('cell-click', r, 1)"
              >
                <div class="cell-number">
                  {{ getCellTotal(r, 1) }}
                </div>
                <div class="cell-label">
                  {{ getSegmentLabel(r, 1) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Eixo X - Recência -->
          <div class="rfm-axis rfm-axis-x">
            <div class="axis-label">
              RECÊNCIA
            </div>
          </div>
        </div>

        <!-- Legenda Dinâmica -->
        <div class="rfm-legend">
          <div
            v-for="(total, segmento) in segmentos"
            :key="segmento"
            class="legend-item clickable"
            :title="`Clique para ver todos os ${total} clientes de ${getSegmentDisplayName(segmento)}`"
            @click="$emit('segment-click', segmento)"
          >
            <div :class="['legend-color', getSegmentClassByName(segmento)]" />
            <span>{{ getSegmentDisplayName(segmento) }} ({{ total }} clientes)</span>
          </div>
        </div>

        <!-- Dica de uso -->
        <div class="text-center mt-3">
          <small class="text-muted">
            💡 <strong>Dica:</strong> Clique em uma célula para ver clientes de uma posição RFM específica,
            ou clique na legenda para ver todos os clientes do segmento.
          </small>
        </div>
      </div> <!-- fim rfm-matrix-container -->
    </b-card>
  </b-col>
</template>

<script>
import { BCol, BCard } from 'bootstrap-vue'
import {
  determinarSegmento,
  getSegmentClass,
  getSegmentDisplayName,
  getSegmentLabel,
  getCellClass,
} from '../services/rfmSegmentation'

export default {
  name: 'RFMAnalysis',
  components: {
    BCol,
    BCard,
  },
  props: {
    matrizRFM: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    segmentos: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  mounted() {
  },
  methods: {
    getCellTotal(recencia, frequencia) {
      const key = `${recencia}-${frequencia}`
      return this.matrizRFM[key]?.total || 0
    },

    /**
     * Retorna o nome do segmento para uma célula (R, F)
     * IMPORTANTE: Usa a lógica centralizada de segmentação 2D
     * para garantir consistência e evitar duplicidades
     */
    getSegmentName(recencia, frequencia) {
      // Usar a lógica centralizada de segmentação 2D
      const segmento = determinarSegmento(recencia, frequencia)
      return segmento.nome
    },

    /**
     * Retorna a classe CSS para uma célula
     */
    getSegmentClass(recencia, frequencia) {
      return getCellClass(recencia, frequencia)
    },

    /**
     * Retorna o label formatado para uma célula
     */
    getSegmentLabel(recencia, frequencia) {
      const segmento = determinarSegmento(recencia, frequencia)
      return getSegmentLabel(segmento.nome)
    },

    /**
     * Retorna a classe CSS pelo nome do segmento
     */
    getSegmentClassByName(segmento) {
      return getSegmentClass(segmento)
    },

    /**
     * Retorna o nome de exibição do segmento
     */
    getSegmentDisplayName(segmento) {
      return getSegmentDisplayName(segmento)
    },
  },
}
</script>

<style src="../styles/rfm-styles.css"></style>
<style scoped>
/* Estilos específicos do componente */
</style>
